import React, { FC, useState } from 'react';
import { useFormik } from 'formik';
import { User } from 'models/user';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Box, TextField, Alert } from '@mui/material';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { CustomerSetting } from 'models/customer-setting';
import { nameRegExp } from 'config/regex';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import LabDefaultClinicAutocomplete from 'components/client-directory/client-settings/dentists/lab-default-clinic-autocomplete.component';

type AddDentistFormProps = {
    addDentist: (dentist: Partial<User>) => void;
    handleClose: () => void;
    shouldCreateWLUser: boolean;
    showAddDentistButton: boolean;
    email: string;
    isIsolatedUsersEnable: boolean;
    labClientCustomerSettings: CustomerSetting[];
    mainClinicId: string;
    initialLabDefaultClinicId?: string;
};

type Dentist = {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    note?: string;
    localId?: string;
    labDefaultClinic?: string;
};

const AddDentistForm: FC<AddDentistFormProps> = ({
    addDentist,
    handleClose,
    email,
    shouldCreateWLUser = false,
    isIsolatedUsersEnable,
    showAddDentistButton,
    labClientCustomerSettings,
    mainClinicId,
    initialLabDefaultClinicId,
}: AddDentistFormProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'users']);
    const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false);
    const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false);

    const whiteListDomains: string[] =
        customerSettings.getCustomerSetting(
            labClientCustomerSettings || [],
            CONSTANTS.SETTINGS_NAMES.ADD_USER_WHITELIST_DOMAINS,
        ) || [];

    // Read from the lab, not from labClientCustomerSettings — that prop holds the clinic's settings.
    const lab = currentOrgVar();
    const isLocalDentistIdAutoAssigned = Boolean(
        customerSettings.getCustomerSetting(
            lab?.customerSettings || [],
            CONSTANTS.SETTINGS_NAMES.AUTO_INCREMENT_LOCAL_DENTIST_ID,
        ),
    );

    const validate = (values: Dentist) => {
        const errors: Record<string, string> = {};
        if (!values.firstName) {
            errors.firstName = translate('common:VALIDATION.FIELD_REQUIRED');
        }
        if (values.firstName && !isFirstNameValid) {
            errors.firstName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.lastName) {
            errors.lastName = translate('common:VALIDATION.FIELD_REQUIRED');
        }
        if (values.lastName && !isLastNameValid) {
            errors.lastName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if ((shouldCreateWLUser || showAddDentistButton) && !values.email) {
            errors.email = translate('common:VALIDATION.FIELD_REQUIRED');
        }
        if ((shouldCreateWLUser || showAddDentistButton) && whiteListDomains.length && values.email) {
            const [_, enteredEmailDomain] = values.email.split('@');
            if (!whiteListDomains.includes(enteredEmailDomain?.toUpperCase())) {
                errors.email = translate('users:VALIDATION:EMAIL_DOMAIN');
            }
        }
        return errors;
    };
    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: email,
            phone: '',
            note: '',
            localId: '',
            labDefaultClinic: '',
        },
        validate,
        onSubmit: async (values: Dentist) => {
            const name = {
                firstName: values.firstName,
                lastName: values.lastName,
            };
            return addDentist({ name, ...values });
        },
    });

    const handleFirstNameChange = (newFirstName: string) => {
        const firstName = newFirstName.charAt(0).toUpperCase() + newFirstName.slice(1);
        setIsFirstNameValid(nameRegExp.test(firstName));
        formik.setFieldValue('firstName', firstName);
    };

    const handleLastNameChange = (newLastName: string) => {
        const lastName = newLastName.charAt(0).toUpperCase() + newLastName.slice(1);
        setIsLastNameValid(nameRegExp.test(lastName));
        formik.setFieldValue('lastName', lastName);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box my={1}>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <TextField
                            id="first-name"
                            fullWidth
                            label={translate('FIRST_NAME')}
                            required
                            margin="dense"
                            helperText={formik.touched.firstName && formik.errors.firstName}
                            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                            onChange={(e) => handleFirstNameChange(e.target.value)}
                            onBlur={formik.getFieldProps('firstName').onBlur}
                            name={formik.getFieldProps('firstName').name}
                            value={formik.getFieldProps('firstName').value}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            id="last-name"
                            fullWidth
                            label={translate('LAST_NAME')}
                            required
                            margin="dense"
                            helperText={formik.touched.lastName && formik.errors.lastName}
                            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                            onChange={(e) => handleLastNameChange(e.target.value)}
                            onBlur={formik.getFieldProps('lastName').onBlur}
                            name={formik.getFieldProps('lastName').name}
                            value={formik.getFieldProps('lastName').value}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            id="email"
                            fullWidth
                            disabled={(shouldCreateWLUser || showAddDentistButton) && !isIsolatedUsersEnable}
                            required={shouldCreateWLUser || showAddDentistButton}
                            label={translate('EMAIL')}
                            type="email"
                            margin="dense"
                            helperText={
                                (shouldCreateWLUser || showAddDentistButton) &&
                                formik.touched.email &&
                                formik.errors.email
                            }
                            error={
                                (shouldCreateWLUser || showAddDentistButton) &&
                                formik.touched.email &&
                                Boolean(formik.errors.email)
                            }
                            {...formik.getFieldProps('email')}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <NumberOnlyTextField
                            id="phone"
                            fullWidth
                            label={translate('PHONE')}
                            maxLength={10}
                            hasDecimal={false}
                            helperText={formik.touched.phone && formik.errors.phone}
                            error={formik.touched.phone && Boolean(formik.errors.phone)}
                            {...formik.getFieldProps('phone')}
                        />
                    </Grid>
                    {/* Renders its own grid cell only when this client is a main clinic with
                        child clinics, so it takes no space otherwise. The suggested clinic is
                        passed as `preferredClinicId` rather than as the initial value: it comes
                        from a clinic picker with a wider filter than this dropdown, so only the
                        dropdown can tell whether it is actually selectable here. */}
                    <LabDefaultClinicAutocomplete
                        mainClinicId={mainClinicId}
                        value={formik.values.labDefaultClinic}
                        preferredClinicId={initialLabDefaultClinicId}
                        onChange={(clinicId) => formik.setFieldValue('labDefaultClinic', clinicId ?? '')}
                    />
                </Grid>
            </Box>
            <Box mb={2}>
                <TextField
                    id="comments"
                    margin="dense"
                    label={translate('users:INTERNAL_DENTIST_NOTE')}
                    multiline
                    minRows={4}
                    maxRows={10}
                    fullWidth
                    {...formik.getFieldProps('note')}
                />
                <Box mt={1}>
                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translate('users:DENTIST_NOTE_INFO')}
                    </Alert>
                </Box>
            </Box>
            <Box mb={2}>
                <TextField
                    id="local-dentist-id"
                    fullWidth
                    label={translate('users:LOCAL_DENTIST_ID')}
                    margin="dense"
                    disabled={isLocalDentistIdAutoAssigned}
                    helperText={isLocalDentistIdAutoAssigned && translate('users:LOCAL_DENTIST_ID_AUTO_ASSIGNED')}
                    {...formik.getFieldProps('localId')}
                />
            </Box>
            <Box mb={1} display="flex" justifyContent="flex-end">
                <Box mr={1}>
                    <Button onClick={handleClose}>{translate('CLOSE')}</Button>
                </Box>
                <Box>
                    <Button variant="contained" disabled={!formik.isValid} type="submit">
                        {translate('orders:ADD_DENTIST')}
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default AddDentistForm;
